import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { useTranslation } from 'react-i18next';
import ResolveImage from 'components/ResolveImage';
import DragScroll from 'components/DragScroll';
import { DropzoneFile } from 'components/Dropzone/utils';
import { getImgUrl } from 'components/ResolveImage/utils';
import { getArrayLength, isArrowLeftPressed, isArrowRightPressed } from 'utils';
import useKeyDown from 'hooks/useKeyDown';

import './PreviewGalleryModal.styles.scss';
import './PreviewGalleryModal.styles.responsive.scss';

export type PreviewGalleryModalRef = {
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
} & ModalRef;

type PreviewGalleryModalProps = {
  gallery: DropzoneFile[];
} & ModalProps;

const PreviewGalleryModal: React.ForwardRefRenderFunction<
  PreviewGalleryModalRef,
  PreviewGalleryModalProps
> = (props, ref) => {
  const { className, gallery = [] } = props;
  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { t } = useTranslation();

  const classes = classNames('preview-gallery-modal', className);

  useImperativeHandle(ref, () => ({ ...modal, setSelectedIndex }), [modal]);

  const renderImages = useMemo(
    () =>
      gallery.map(({ key, name, url }, index) => (
        <ResolveImage
          className={classNames({ active: index === selectedIndex })}
          key={`p-modal-${key}`}
          src={getImgUrl(url, '480')}
          alt={name}
          rootProps={{
            onClick: () => {
              setSelectedIndex(index);
            },
          }}
        />
      )),
    [gallery, selectedIndex],
  );

  const renderMainImage = useMemo(() => {
    if (!gallery[selectedIndex]) return null;

    const { key, url, name } = gallery[selectedIndex];
    return (
      <ResolveImage
        className="preview-gallery-modal__main-image"
        key={key}
        src={getImgUrl(url, '720')}
        alt={name}
      />
    );
  }, [gallery, selectedIndex]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.isComposing) return;

      if (isArrowLeftPressed(event)) {
        setSelectedIndex((old) => (old ? old : getArrayLength(gallery)) - 1);
        return;
      }

      if (isArrowRightPressed(event))
        setSelectedIndex((old) =>
          old === getArrayLength(gallery) - 1 ? 0 : old + 1,
        );
    },
    [gallery],
  );

  useKeyDown({ onKeyDown });

  return (
    <Modal ref={modalRef} className={classes} title={t('gallery')}>
      {renderMainImage}
      <DragScroll
        adjustToMobile
        wrapperClassname="preview-gallery-modal__images"
      >
        {renderImages}
      </DragScroll>
    </Modal>
  );
};

export default forwardRef(PreviewGalleryModal);
